/**
 * Helper methods to quickly create filter group objects used in crm-search.
 * Reference: https://product.hubteam.com/docs/crm-search/usage.html#filters-and-filter-groups
 */
import {
  FilterOperator,
  RangeOperatorOptions,
  RollingDateRangeOperator,
  FilterOperatorValue,
  TimeUnitToDateOperator,
} from 'marketing-platform-lib/types';

/**
 * Finds objects where the chosen property value is equal to the input value.
 * @param {string} property
 * @param {string|number|boolean} value
 */
export function EQ(
  property: string,
  value: FilterOperatorValue
): FilterOperator {
  return {
    operator: 'EQ',
    property,
    value,
  };
}

/**
 * Finds objects where the chosen property value is not equal to the input value.
 * @param {string} property
 * @param {string|number|boolean} value
 */
export function NEQ(
  property: string,
  value: FilterOperatorValue
): FilterOperator {
  return {
    operator: 'NEQ',
    property,
    value,
  };
}

/**
 * Finds objects where the chosen property does not have a value.
 * @param {string} property
 */
export function NOT_HAS_PROPERTY(property: string): FilterOperator {
  return {
    operator: 'NOT_HAS_PROPERTY',
    property,
  };
}

/**
 * Finds objects where the chosen property has a value.
 * @param {string} property
 */
export function HAS_PROPERTY(property: string): FilterOperator {
  return {
    operator: 'HAS_PROPERTY',
    property,
  };
}

/**
 * Finds objects where the chosen property value does not exactly match any of the input values.
 * @param {string} property
 * @param {string[]|number[]|boolean[]} values
 */
export function NOT_IN(
  property: string,
  values: FilterOperatorValue[]
): FilterOperator {
  return {
    operator: 'NOT_IN',
    property,
    values,
  };
}

/**
 * Finds objects where the chosen property value is between value and highValue.
 * @param {string} property
 * @param {string|number|boolean} value
 * @param {object} options
 */
export function BETWEEN(
  property: string,
  value: FilterOperatorValue,
  highValue: FilterOperatorValue,
  options?: RangeOperatorOptions
): FilterOperator {
  return {
    highValue,
    operator: 'BETWEEN',
    property,
    value,
    ...(options || {}),
  };
}

/**
 * Finds objects where the chosen property value is less than or equals value.
 * @param {string} property
 * @param {string|number|boolean} value
 * @param {object} options
 */
export function LTE(
  property: string,
  value: FilterOperatorValue,
  options?: RangeOperatorOptions
): FilterOperator {
  return {
    operator: 'LTE',
    property,
    value,
    ...(options || {}),
  };
}

/**
 * Finds objects where the chosen property value is less than value.
 * @param {string} property
 * @param {string|number|boolean} value
 * @param {object} options
 */
export function LT(
  property: string,
  value: FilterOperatorValue,
  options?: RangeOperatorOptions
): FilterOperator {
  return {
    operator: 'LT',
    property,
    value,
    ...(options || {}),
  };
}

/**
 * Finds objects where the chosen property value is greater than or equals value.
 * @param {string} property
 * @param {string|number|boolean} value
 * @param {object} options
 */
export function GTE(
  property: string,
  value: FilterOperatorValue,
  options?: RangeOperatorOptions
): FilterOperator {
  return {
    operator: 'GTE',
    property,
    value,
    ...(options || {}),
  };
}

/**
 * Finds objects where the chosen property value is greater than value.
 * @param {string} property
 * @param {string|number|boolean} value
 * @param {object} options
 */
export function GT(
  property: string,
  value: FilterOperatorValue,
  options?: RangeOperatorOptions
): FilterOperator {
  return {
    operator: 'GT',
    property,
    value,
    ...(options || {}),
  };
}

/**
 * Finds objects where the chosen property value is in any of the input values.
 * @param {string} property
 * @param {string[]|number[]|boolean[]} values
 */
export function IN(
  property: string,
  values: FilterOperatorValue[]
): FilterOperator {
  return {
    operator: 'IN',
    property,
    values,
  };
}

/**
 * Find objects where a chosen date(time) property is within a date range with relation to the current time.
 * @param {string} property
 * @param {Object} options
 */
export function ROLLING_DATE_RANGE(
  property: string,
  options: Pick<
    RollingDateRangeOperator,
    'inclusive' | 'rollForward' | 'rollingOffset' | 'timeUnit' | 'timeUnitCount'
  >
): FilterOperator {
  return {
    operator: 'ROLLING_DATE_RANGE',
    property,
    ...options,
  };
}

/**
 * Find objects where a chosen time property is within a date range with relation to the current time.
 * @param {string} property
 * @param {Object} options
 */
export function TIME_UNIT_TO_DATE(
  property: string,
  options: Pick<
    TimeUnitToDateOperator,
    'inclusive' | 'rollForward' | 'rollingOffset' | 'timeUnit' | 'timeUnitCount'
  >
): FilterOperator {
  return {
    operator: 'TIME_UNIT_TO_DATE',
    property,
    ...options,
  };
}

/**
 * Find objects where a chosen date(time) property is outside the last X days.
 * @param {string} property
 * @param {string[]|number[]|boolean[]} value
 * @param {Object} options
 */
export function GT_X_DAYS(
  property: string,
  value: FilterOperatorValue,
  options: {
    upperBoundInclusive?: boolean;
    lowerBoundInclusive?: boolean;
  }
): FilterOperator {
  return {
    operator: 'GT_X_DAYS',
    property,
    value,
    ...options,
  };
}

/**
 * Find objects where a chosen date(time) property is within the last X days.
 * @param {string} property
 * @param {string[]|number[]|boolean[]} value
 * @param {Object} options
 */
export function LT_X_DAYS(
  property: string,
  value: FilterOperatorValue,
  options: {
    upperBoundInclusive?: boolean;
    lowerBoundInclusive?: boolean;
  }
): FilterOperator {
  return {
    operator: 'LT_X_DAYS',
    property,
    value,
    ...options,
  };
}

/**
 * Simple validator for filter operators
 */
export function isFilterOperator(
  maybeFilterOperator: object | undefined | null
) {
  if (!maybeFilterOperator || typeof maybeFilterOperator !== 'object') {
    return false;
  }

  if (
    !('operator' in maybeFilterOperator) ||
    !('property' in maybeFilterOperator)
  ) {
    return false;
  }

  return true;
}
